
import React, { useState } from 'react';
import { Toaster } from 'sonner';
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./lib/supabase";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { AppLayout } from "./components/layout/AppLayout";
import MyBots from "./pages/MyBots";
import BotDetailPage from "./pages/BotDetailPage";
import PremiumBots from "./pages/PremiumBots";
import PremiumBotDetailPage from "./pages/PremiumBotDetailPage";
import AccountsPage from "./pages/AccountsPage";
import ManageCTraderProfiles from "./pages/ManageCTraderProfiles";
import CTraderProfileDetail from "./pages/CTraderProfileDetail";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AuthCallback from "./pages/auth/AuthCallback";
import { AuthProvider } from "./contexts/AuthContext";
import { TourProvider } from "./contexts/TourContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import PropTradingPage from "./pages/PropTradingPage";
import CommunityPage from "./pages/CommunityPage";
import SettingsPage from "./pages/SettingsPage";
import UserManagementPage from "./pages/admin/UserManagementPage";
import AdminPositionMappings from "./pages/admin/AdminPositionMappings";
import PropTradingBotDetailPage from "./pages/PropTradingBotDetailPage";
import AiAssistantPage from "./pages/AiAssistantPage";
import { AiAssistantLayout } from "./components/ai-assistant/layout/AiAssistantLayout";
import { UpdateNotification } from './components/common/UpdateNotification';
import TableCalculatorPage from "./pages/TableCalculatorPage";
import MarketAnalysisPage from "./pages/MarketAnalysisPage";
import MarketAnalysisHistoryPage from "./pages/MarketAnalysisHistoryPage";
import { MarketAnalysisLayoutNew } from "./components/market-analysis/layout/MarketAnalysisLayoutNew";
import MarketDataAnalysisPage from './pages/admin/MarketDataAnalysisPage';
import MarketAlertSettingsPage from "./pages/admin/MarketAlertSettingsPage";
import MarketAnalysisDetailPage from "./pages/MarketAnalysisDetailPage";

// Admin components
import AdminDashboard from "./pages/admin/AdminDashboard";
import AppSettingsPage from "./pages/admin/AppSettingsPage";
import ConditionalSkipPage from "./pages/admin/ConditionalSkipPage";
import { AdminLayout } from "./components/admin/AdminLayout";
import { AppRefreshManagement } from "./components/admin/AppRefreshManagement";
import PropBotManagementPage from './pages/admin/prop-trading/PropBotManagementPage';
import PropBotAddPage from './pages/admin/prop-trading/PropBotAddPage';
import PropBotEditPage from './pages/admin/prop-trading/PropBotEditPage';
import PremiumBotManagementPage from './pages/admin/premium-bots/PremiumBotManagementPage';
// New premium bot pages
import PremiumBotAddPage from './pages/admin/premium-bots/PremiumBotAddPage';
import PremiumBotEditPage from './pages/admin/premium-bots/PremiumBotEditPage';
import TradingViewCrawlerPage from './pages/admin/TradingViewCrawlerPage';
import TradingPatternAnalysisPage from './pages/admin/TradingPatternAnalysisPage';
import { WLLSAnalysisPage } from './pages/admin/WLLSAnalysisPage';
import { TrackingBotSignal } from './pages/admin/tracking-bot-signal';
import CsvAnalysisPage from './pages/user/CsvAnalysis';
import RiskCalculatorPage from './pages/RiskCalculatorPage';
import MiniAppsPage from './pages/MiniAppsPage';

import TradingAccountsPage from './pages/admin/TradingAccountsPage';
import { CTraderSignalManagementPage } from './components/admin/ctrader-signals/CTraderSignalManagementPage';
import BotCheckerPage from './pages/admin/BotCheckerPage';
import BotAccountErrorsPage from './pages/admin/BotAccountErrorsPage';
import TelegramBotManagementPage from './pages/admin/TelegramBotManagementPage';

// AI Assistant Admin Pages - moved to general admin
import AiAssistantManagementPage from "./pages/admin/ai-assistant/AiAssistantManagementPage";
import AiAssistantAnalyticsPage from "./pages/admin/ai-assistant/AiAssistantAnalyticsPage";

// Merge Bots
import { MergeBots } from "./pages/admin/MergeBots";

// Affiliate Pages
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import AffiliateOnboarding from "./pages/affiliate/AffiliateOnboarding";
import AffiliateLinks from "./pages/affiliate/AffiliateLinks";
import AffiliateResources from "./pages/affiliate/AffiliateResources";
import { AffiliateSettings } from "./pages/affiliate/AffiliateSettings";
import AffiliateReferrals from "./pages/affiliate/AffiliateReferrals";
import ManageUserLimitsPage from "./pages/affiliate/ManageUserLimitsPage";

// Affiliate Admin Pages - moved to general admin
import AffiliateAdminDashboard from "./pages/admin/affiliate/AffiliateAdminDashboard";
import ManageAffiliatesPage from "./pages/admin/affiliate/ManageAffiliatesPage";
import ReferralTrackingPage from "./pages/admin/affiliate/ReferralTrackingPage";
import FlaggedActivityPage from "./pages/admin/affiliate/FlaggedActivityPage";
import AffiliateLimitsPage from "./pages/admin/affiliate/AffiliateLimitsPage";
import AffiliateBackfillPage from "./pages/admin/affiliate/AffiliateBackfillPage";
import EmailNotificationsPage from "./pages/admin/EmailNotificationsPage";

// Affiliate Layout
import { AffiliateLayout } from "./components/affiliate/layout/AffiliateLayout";
import { OnboardingLayout } from "./components/affiliate/layout/OnboardingLayout";

import ReferralLinkHandler from "./components/affiliate/ReferralLinkHandler";
import { AiPropAutoVisitor } from "./components/ai-prop/AiPropAutoVisitor";

import { HelmetProvider } from 'react-helmet-async';

import { useLogoPreloader } from './hooks/useLogoPreloader';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Remove console logs to keep customer console clean
supabase.auth.onAuthStateChange((event, session) => {
  // Auth state change handling without logging
});

function App() {
  const [queryClient] = useState(() => new QueryClient());
  
  // Initialize logo preloader
  useLogoPreloader();
  
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          forcedTheme="dark"
          enableSystem={false}
          disableTransitionOnChange
        >
          <ReferralLinkHandler />
          <AiPropAutoVisitor />
          
          <TooltipProvider>
            <Toaster 
              position="bottom-right"
              theme="dark"
              className="dark"
            />
            <UpdateNotification />
            
            <AuthProvider>
            <TourProvider>
              <Routes>
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/register" element={<Register />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Table Calculator Page - Standalone without AppLayout */}
                <Route 
                  path="/table" 
                  element={
                    <ProtectedRoute>
                      <TableCalculatorPage />
                    </ProtectedRoute>
                  } 
                />

                {/* Market Analysis AI - Changed to ProtectedRoute to allow affiliate access */}
                <Route 
                  path="/market-analysis" 
                  element={
                    <ProtectedRoute>
                      <MarketAnalysisLayoutNew>
                        <MarketAnalysisPage />
                      </MarketAnalysisLayoutNew>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Market Analysis History - Changed to ProtectedRoute to allow affiliate access */}
                <Route 
                  path="/market-analysis/history" 
                  element={
                    <ProtectedRoute>
                      <MarketAnalysisLayoutNew>
                        <MarketAnalysisHistoryPage />
                      </MarketAnalysisLayoutNew>
                    </ProtectedRoute>
                  } 
                />

                {/* Market Analysis Detail - Changed to ProtectedRoute to allow affiliate access */}
                <Route 
                  path="/market-analysis/history/:id" 
                  element={
                    <ProtectedRoute>
                      <MarketAnalysisLayoutNew>
                        <MarketAnalysisDetailPage />
                      </MarketAnalysisLayoutNew>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Market Data Analysis AI - Admin only with AdminLayout */}
                <Route 
                  path="/admin/market-data-analysis" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <MarketDataAnalysisPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* Market Alert Settings - Admin only */}
                <Route 
                  path="/admin/market-alert-settings" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <MarketAlertSettingsPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* CTrader Account Management - Admin only */}
                <Route 
                  path="/admin/ctrader-accounts" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <TradingAccountsPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* Dashboard Routes */}
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Index />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* My Bots routes - Admin only */}
                <Route 
                  path="/my-bots" 
                  element={
                    <AdminProtectedRoute>
                      <AppLayout>
                        <MyBots />
                      </AppLayout>
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/bots/:botId" 
                  element={
                    <AdminProtectedRoute>
                      <AppLayout>
                        <BotDetailPage />
                      </AppLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Regular user routes */}
                <Route 
                  path="/premium-bots" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PremiumBots />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/premium-bots/:botId" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PremiumBotDetailPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* --- NEW: Add marketplace routes for Premium Bots */}
                <Route 
                  path="/marketplace" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PremiumBots />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/marketplace/:botId"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PremiumBotDetailPage />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
                {/* --- END new marketplace premium bot routes */}

                <Route 
                  path="/exchange-accounts" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <AccountsPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/manage-ctrader-profiles" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ManageCTraderProfiles />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ctrader-profiles/:id" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <CTraderProfileDetail />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/prop-trading" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PropTradingPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/prop-trading/:botId" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <PropTradingBotDetailPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/community" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <CommunityPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                 <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <SettingsPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* CSV Analysis Route */}
                <Route 
                  path="/user/csv-analysis" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <CsvAnalysisPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Risk Calculator Route */}
                <Route 
                  path="/risk-calculator" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <RiskCalculatorPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Mini Apps Route */}
                <Route 
                  path="/mini-apps" 
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <MiniAppsPage />
                      </AppLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* AI Assistant Routes - Only user-facing routes remain here */}
                <Route 
                  path="/ai-assistant" 
                  element={
                    <ProtectedRoute>
                      <AiAssistantLayout>
                        <AiAssistantPage />
                      </AiAssistantLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/ai-assistant/:category" 
                  element={
                    <ProtectedRoute>
                      <AiAssistantLayout>
                        <AiAssistantPage />
                      </AiAssistantLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/ai-assistant/:category/:session_id" 
                  element={
                    <ProtectedRoute>
                      <AiAssistantLayout>
                        <AiAssistantPage />
                      </AiAssistantLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* Affiliate Routes - Regular affiliate access */}
                <Route 
                  path="/affiliate" 
                  element={
                    <ProtectedRoute>
                      <AffiliateLayout>
                        <AffiliateDashboard />
                      </AffiliateLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/affiliate/onboarding" 
                  element={
                    <ProtectedRoute>
                      <OnboardingLayout>
                        <AffiliateOnboarding />
                      </OnboardingLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Removed the /affiliate/reports route */}
                
                <Route 
                  path="/affiliate/links" 
                  element={
                    <ProtectedRoute>
                      <AffiliateLayout>
                        <AffiliateLinks />
                      </AffiliateLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* New Affiliate User Limits Management Route */}
                <Route 
                  path="/affiliate/manage-limits" 
                  element={
                    <ProtectedRoute>
                      <AffiliateLayout>
                        <ManageUserLimitsPage />
                      </AffiliateLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Removed the /affiliate/resources route */}
                
                <Route 
                  path="/affiliate/settings" 
                  element={
                    <ProtectedRoute>
                      <AffiliateLayout>
                        <AffiliateSettings />
                      </AffiliateLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* New route for referred users */}
                <Route 
                  path="/affiliate/referrals" 
                  element={
                    <ProtectedRoute>
                      <AffiliateLayout>
                        <AffiliateReferrals />
                      </AffiliateLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* NEW CENTRALIZED ADMIN ROUTES */}
                {/* Main Admin Route - Redirect to Trading Pattern Analysis */}
                <Route 
                  path="/admin" 
                  element={<Navigate to="/admin/trading-pattern-analysis" replace />}
                />
                
                {/* Admin Dashboard - Alternative route */}
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* Signal CTrader Management Route */}
                <Route 
                  path="/admin/ctrader-signals" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <CTraderSignalManagementPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* App Settings Route - contains both logo management and AI prop settings */}
          <Route 
            path="/admin/users" 
            element={
              <AdminProtectedRoute>
                <AdminLayout>
                  <UserManagementPage />
                </AdminLayout>
              </AdminProtectedRoute>
            } 
          />
          <Route 
            path="/admin/app-settings" 
            element={
              <AdminProtectedRoute>
                <AdminLayout>
                  <AppSettingsPage />
                </AdminLayout>
              </AdminProtectedRoute>
            } 
          />

                {/* App Refresh Management Route */}
                <Route 
                  path="/admin/app-refresh" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <AppRefreshManagement />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* TradingView Crawler Route */}
                <Route 
                  path="/admin/tradingview-crawler" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <TradingViewCrawlerPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Trading Pattern Analysis Route */}
                <Route 
                  path="/admin/trading-pattern-analysis" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <TradingPatternAnalysisPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* WLLS Analysis Route */}
                <Route 
                  path="/admin/wlls" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <WLLSAnalysisPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Bot Signal Tracking Route */}
                <Route 
                  path="/admin/tracking-bot-signal" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <TrackingBotSignal />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Bot Merging Route */}
                <Route 
                  path="/admin/merge-bots" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <MergeBots />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Conditional Skip Route */}
                <Route 
                  path="/admin/conditional-skip" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <ConditionalSkipPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                
                {/* Redirect old logo management route to app settings */}
                <Route 
                  path="/admin/logo-management" 
                  element={<Navigate to="/admin/app-settings" replace />}
                />
                
                {/* Premium Bot Management Routes */}
                <Route 
                  path="/admin/premium-bots" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PremiumBotManagementPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* New premium bot routes */}
                <Route 
                  path="/admin/premium-bots/new" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PremiumBotAddPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/premium-bots/edit/:id" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PremiumBotEditPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                {/* Prop Trading Bot Management Routes */}
                <Route 
                  path="/admin/prop-trading" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PropBotManagementPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/prop-trading/new" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PropBotAddPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/prop-trading/edit/:id" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <PropBotEditPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/email-notifications" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <EmailNotificationsPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* AI Assistant Admin Routes - moved under /admin/ai-assistant */}
                <Route 
                  path="/admin/ai-assistant/management" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AiAssistantManagementPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/ai-assistant/analytics" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AiAssistantAnalyticsPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Position Mappings Admin Route */}
                <Route 
                  path="/admin/position-mappings" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AdminPositionMappings />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* Bot Checker Admin Route */}
                <Route 
                  path="/admin/bot-checker" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <BotCheckerPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Bot Account Errors Admin Route */}
                <Route 
                  path="/admin/bot-errors" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <BotAccountErrorsPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Telegram Bot Management Route */}
                <Route 
                  path="/admin/telegram-bot-management" 
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout>
                        <TelegramBotManagementPage />
                      </AdminLayout>
                    </AdminProtectedRoute>
                  } 
                />

                {/* Affiliate Admin Routes - moved under /admin/affiliate */}
                <Route 
                  path="/admin/affiliate" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AffiliateAdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/affiliate/affiliates" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ManageAffiliatesPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/affiliate/tracking" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <ReferralTrackingPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/affiliate/flagged" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <FlaggedActivityPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* New Affiliate Limits Management Route */}
                <Route 
                  path="/admin/affiliate/limits" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AffiliateLimitsPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />

                {/* New Affiliate Backfill Tool Route */}
                <Route 
                  path="/admin/affiliate/backfill" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <AffiliateBackfillPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </TourProvider>
          </AuthProvider>
        </TooltipProvider>
        </ThemeProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
